import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import type { ILeadFormModel } from '../../../typings'
import { useGetSalesTeamPermissionMessages } from '@user/store/salesTeam/hooks'
import { useSearchAssignmentRule } from '@user/components/Settings/LeadAssignmentRule/hooks/useSearchAssignmentRule'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { SalesTeam } from '@user/store/salesTeam'
import { selectAllSalesTeamsUsers } from '@user/store/salesTeam/slice'
import { useGetSalesTeamInputLabel } from '@user/components/Settings/SalesTeams/hooks'
import {
  ISalesFunnel,
  type ISalesFunnelStage,
  useGetEntityCloseReasonsOnMount,
} from '@user/store/salesFunnel'
import { ENTITY_TYPES } from '@sales-center/common'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import {
  areAssignmentFieldsLocked,
  isOwnerFieldLocked,
  isSalesTeamFieldLocked,
  salesTeamIdForOwner,
} from '@user/components/Settings/LeadAssignmentRule/ownerFieldPolicy'

type Props = {
  branchId?: string
  disabled?: boolean
  salesTeams: SalesTeam[]
  salesFunnelStages: ISalesFunnelStage[]
}

export function useData(props: Props) {
  const { disabled, branchId, salesTeams, salesFunnelStages } = props
  const [isOverriden, setIsOverriden] = useState<boolean>(false)
  const [invalidBranch, setInvalidBranch] = useState<boolean>(false)
  const [ignoreDefault, setIgnoreDefault] = useState<boolean>(false)
  const { assignmentRule: rule, hasRemoteAssignmentRule } =
    useSearchAssignmentRule(isOverriden)

  const currentUser = useSelector(selectCurrentUser)
  // The plural selector: state.salesTeam.salesTeamsUsers, which spans teams. The singular one
  // this used to read is setAll'd per team, so it held only the selected team -- which made
  // salesTeamIdForOwner below return null on every call, since the owner it was asked about was
  // either absent from the list or already on the current team. "The owner's own sales team
  // follows them" never once fired.
  //
  // Still one row per PERSON, not per membership: both adapters key on the user id, so someone on
  // several teams collapses to a single row and the policy's preference among their teams cannot
  // be exercised. It picks a team they are genuinely on, which is the part that was broken.
  const allTenantUsers = useSelector(selectAllSalesTeamsUsers)
  const isGuestUser = currentUser?.roleName === guestRoleName

  const canOverrideAutomatedAssignment = useMemo(() => {
    return [
      'leads_override_automated_assignment',
      'workwave_marketing_admin',
    ].some((permission) => currentUser.permissions.includes(permission))
  }, [currentUser.permissions])

  const salesOwnerShipDisabled = useMemo(
    () =>
      isOwnerFieldLocked({
        explicitlyDisabled: Boolean(disabled),
        rule,
        canOverrideAutomatedAssignment,
      }),
    [disabled, canOverrideAutomatedAssignment, rule],
  )

  // Separate from the owner lock on purpose -- see isSalesTeamFieldLocked. Aliasing the two tied
  // the team restriction to the owner COUNT, so a conventional rule naming one team and either
  // no owners or two of them stopped locking the team for a rep who cannot override it.
  const salesTeamLocked = useMemo(
    () =>
      isSalesTeamFieldLocked({
        explicitlyDisabled: Boolean(disabled),
        rule,
        canOverrideAutomatedAssignment,
      }),
    [disabled, canOverrideAutomatedAssignment, rule],
  )

  // Sales funnel and lead status are NOT owner fields, and must not follow the owner policy.
  //
  // That policy deliberately unlocks when several owners are eligible ("a real choice to make")
  // and whenever the assignment came from a territory. Both are right for the owner field and
  // wrong here: a rule that approves two owners still dictates one funnel and one status, so
  // routing these through it quietly handed a rep without override permission a funnel and status
  // that were read-only before this branch.
  //
  // This is what master gated them on -- a rule applies and you may not override it -- minus the
  // territory case, which is a suggestion about WHO, carries no funnel or status of its own, and
  // has no business locking fields it says nothing about.
  const assignmentFieldsLocked = useMemo(
    () =>
      areAssignmentFieldsLocked({
        explicitlyDisabled: Boolean(disabled),
        rule,
        canOverrideAutomatedAssignment,
      }),
    [disabled, canOverrideAutomatedAssignment, rule],
  )

  const {
    setValues,
    setFieldValue,
    values: {
      salesTeamId,
      assigneeId,
      salesFunnelId,
      salesFunnelStageId,
      salesFunnelStageName,
    },
    errors,
    touched,
  } = useFormikContext<ILeadFormModel>()

  const onChangeOwnerShip = useCallback(
    (value?: any) => {
      if (
        !isOverriden &&
        rule?.approvedOwnerIds &&
        rule.approvedOwnerIds.length > 0 &&
        !rule.approvedOwnerIds.includes(value.id)
      ) {
        setIsOverriden(true)
      }
    },
    [isOverriden],
  )

  const onChangeOwner = useCallback(
    (value?: any) => {
      if (!isOverriden && value?.id) {
        const isMatchingRule =
          rule?.approvedOwnerIds &&
          rule.approvedOwnerIds.length > 0 &&
          rule.approvedOwnerIds.includes(value.id)

        if (!isMatchingRule) {
          setIsOverriden(true)
        }
      }

      // The owner's own sales team follows them (WWM-17508). Eligible owners can span teams --
      // two overlapping territories may belong to different ones -- and leaving the team behind
      // would file the lead under a team the chosen owner is not on. Only moves when the owner
      // actually belongs elsewhere, so the ordinary single-team case never sees this.
      const nextTeam = salesTeamIdForOwner(
        allTenantUsers,
        value?.id,
        salesTeamId,
        rule?.approvedSalesTeamIds,
      )
      if (nextTeam) setFieldValue('salesTeamId', nextTeam)
    },
    [isOverriden, rule, allTenantUsers, salesTeamId, setFieldValue],
  )

  const onChangeSalesTeam = useCallback(
    async (value: SalesTeam) => {
      if (
        !isOverriden &&
        rule?.approvedSalesTeamIds &&
        rule.approvedSalesTeamIds.length > 0 &&
        !rule.approvedSalesTeamIds.includes(value.id)
      ) {
        setIsOverriden(true)
      }
      if (value) {
        const assigneeId =
          (currentUser.permissions.includes('leads_own') &&
            currentUser.salesTeamPositionsData?.salesTeamIds.includes(
              value.id,
            )) ||
          currentUser.permissions.includes('workwave_marketing_admin')
            ? currentUser.userId
            : ''
        await setFieldValue('assigneeId', assigneeId)
        setIgnoreDefault(true)
      }
    },
    [
      currentUser.permissions,
      currentUser.salesTeamPositionsData?.salesTeamIds,
      currentUser.userId,
      isOverriden,
      rule,
      setFieldValue,
    ],
  )

  const onChangeSalesFunnel = useCallback(
    (value: ISalesFunnel) => {
      onChangeOwnerShip()

      setValues((v) => ({
        ...v,
        salesFunnelStageId: '',
        salesFunnelId: value.id,
      }))
    },
    [onChangeOwnerShip, setValues],
  )

  const { positionBasedAccessText, selectedSalesTeamAccessText } =
    useGetSalesTeamPermissionMessages('success', +salesTeamId, assigneeId)

  const salesTeamsFiltered = useMemo(
    () =>
      branchId ? salesTeams.filter((x) => x.branchId == branchId) : salesTeams,
    [branchId, salesTeams],
  )

  const sortedOptions = salesTeamsFiltered
    ?.filter((x) => {
      if (
        !canOverrideAutomatedAssignment &&
        rule &&
        rule.approvedSalesTeamIds &&
        rule.approvedSalesTeamIds.length > 0
      ) {
        return rule?.approvedSalesTeamIds?.includes(x.id)
      }
      return true
    })
    ?.sort((a, b) => {
      const labelA = a?.name?.toLowerCase()
      const labelb = b?.name?.toLowerCase()
      if (labelA < labelb) {
        return -1
      }
      if (labelA > labelb) {
        return 1
      }
      return 0
    })

  const defaultOwnerId = useMemo(
    () =>
      !disabled &&
      !hasRemoteAssignmentRule &&
      salesTeamId &&
      ((currentUser.permissions.includes('leads_own') &&
        salesTeamsFiltered?.length == 1 &&
        currentUser.salesTeamPositionsData?.salesTeamIds.includes(
          salesTeamsFiltered[0].id,
        )) ||
        currentUser.permissions.includes('workwave_marketing_admin'))
        ? currentUser.userId
        : undefined,
    [
      currentUser.permissions,
      currentUser.salesTeamPositionsData?.salesTeamIds,
      currentUser.userId,
      disabled,
      hasRemoteAssignmentRule,
      salesTeamId,
      salesTeamsFiltered,
    ],
  )

  const selectedLabel = useGetSalesTeamInputLabel(
    +salesTeamId,
    salesTeamsFiltered,
  )

  const { reasonDetail } = useGetEntityCloseReasonsOnMount({
    entityType: ENTITY_TYPES.Lead,
    salesFunnelId: Number(salesFunnelId),
  })

  useEffect(() => {
    setFieldValue(
      'salesFunnelStageName',
      salesFunnelStages.find((value) => value.id === Number(salesFunnelStageId))
        ?.name,
    )
  }, [salesFunnelStageId, setFieldValue, salesFunnelStages])

  useEffect(() => {
    const team = sortedOptions.find((item) => item.id == Number(salesTeamId))
    if (rule && hasRemoteAssignmentRule) {
      setInvalidBranch(Boolean(!team))
      const newSalesTeamId = !team ? '' : salesTeamId
      // Only update if the value actually needs to change to prevent infinite loop
      if (newSalesTeamId !== salesTeamId) {
        setFieldValue('salesTeamId', newSalesTeamId)
      }
    }
  }, [hasRemoteAssignmentRule, rule, salesTeamId, setFieldValue, sortedOptions])

  const reasonIdError = Boolean(errors.reasonId && touched.reasonId)
  const reasonIdErrorTxt = reasonIdError ? errors.reasonId : ''

  const customReasonError = Boolean(errors.customReason && touched.customReason)
  const customReasonErrorTxt = customReasonError ? errors.customReason : ''

  const disableAssignmentRule = isGuestUser && hasRemoteAssignmentRule

  return {
    isOverriden,
    setIsOverriden,
    salesOwnerShipDisabled,
    salesTeamLocked,
    assignmentFieldsLocked,
    onChangeOwnerShip,
    onChangeOwner,
    onChangeSalesTeam,
    onChangeSalesFunnel,
    positionBasedAccessText,
    selectedSalesTeamAccessText,
    salesTeamsFiltered,
    sortedOptions,
    rule,
    selectedLabel,
    disqualifyReasons: reasonDetail?.disqualifyReasons || [],
    allowManualDisqualifyReasons: reasonDetail?.allowManualDisqualifyReasons,
    leadStatus: salesFunnelStageName,
    reasonIdError,
    reasonIdErrorTxt,
    customReasonError,
    customReasonErrorTxt,
    invalidBranch,
    defaultOwnerId,
    ignoreDefault,
    disableAssignmentRule,
    canOverrideAutomatedAssignment,
  }
}

export type UseData = ReturnType<typeof useData>
